/**
 * Générateur — Démontrer une propriété générale par le calcul littéral.
 * Attendu ID : 938 | Niveau : 5ème | Domaine : Calcul littéral (cluster 6, O40)
 *
 * Source : REPLI SÉSAMATH (décision utilisateur 2026-07-11 — pas d'exercice
 * dans le doc prof). Cahier Sésamath 5e chap. 05, Série 5 « Démontrer une
 * égalité » (p72), ex. 1-3, 5, 7-9 + cadrage officiel (somme de deux pairs).
 *
 * Anti-doublons : les gestes développer/réduire eux-mêmes sont portés par
 * gen_936/937 — ici le geste noté est la PREUVE (justifier les étapes,
 * statut universel d'une égalité, preuves de parité, exemple ≠ démonstration).
 * L'ex. 6 (« trouve deux expressions égales pour une valeur… », ouvert) est
 * porté par gen_939 (carte « égales parfois »).
 *
 * Plan de cartes (validé 2026-07-12) :
 *
 *   Carte « justifier » — ex.3 : texte_trou_drag (chaîne A = k(x±m) + px,
 *   chaque ligne justifiée : j'ai {{développé}} / j'ai {{réduit}} —
 *   étiquettes développé/réduit/factorisé/substitué/testé) + qcm_unique
 *   (l'ordre des gestes ; distracteur central : « remplacer x par une
 *   valeur suffit à démontrer »).
 *
 *   Carte « corrige » — ex.5 : égalités UNIVERSELLES (≠ gen_934 qui teste en
 *   UN point) : tableau_vf « vraie pour TOUTE valeur de x ? » — vraies par
 *   construction (développement/réduction corrects), fausses par
 *   misconception (parenthèses supprimées : 2(x+3)+1 = 2x+4 ; termes non
 *   semblables fusionnés : ax + c = (a+c)x). Vérité décidable en évaluant en
 *   2 points (expressions affines). + qcm_unique justification.
 *
 *   Carte « parite » — ex.7/8 + cadrage : ordonner (les 4 étapes de la
 *   preuve — écrire 2n/2p+1, calculer, factoriser, conclure — 4 propriétés
 *   en rotation : somme 2 pairs, somme 2 impairs, pair+impair, produit
 *   pair×impair ; lettres et prénoms en rotation pour la variabilité) +
 *   texte_trou_drag (compléter la preuve).
 *
 *   Carte « preuve » — ex.9, LE point du cadrage : qcm_unique « untel a
 *   vérifié sur 3 exemples : a-t-il démontré ? » (non — un calcul littéral
 *   démontre, des exemples vérifient) + tableau_vf (statut des preuves :
 *   exemples/contre-exemple/calcul littéral — habillé de prénoms et de
 *   nombres pour la variabilité).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeOrdonner, filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 938;

const L_JUSTIFIER = 'Justifier chaque étape d\'un calcul littéral';
const L_CORRIGE = 'Vraie pour toute valeur de x ?';
const L_PARITE = 'Démontrer : pairs et impairs';
const L_PREUVE = 'Exemple ou démonstration ?';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const MOINS = '−';   // U+2212 (affichage uniquement)

const PRENOMS = ['Lina', 'Noah', 'Sofia', 'Adam', 'Jade', 'Tom'];
// pronom sujet pour les tournures « A-t-il/elle démontré ? »
const PRONOM = { Lina: 'elle', Noah: 'il', Sofia: 'elle', Adam: 'il', Jade: 'elle', Tom: 'il' };

// ═════════ Carte 1 — Justifier chaque étape (ex.3) ═════════

const tirerChaine = () => {
    const L = randomChoice(['x', 'y', 'n']);
    const k = randInt(2, 6);
    const m = randInt(2, 9);
    const p = randInt(2, 9);
    const op = randomChoice(['+', MOINS]);
    const ordre = randomChoice(['devant', 'derriere']);   // k(L±m) + pL ou pL + k(L±m)
    const nom = randomChoice(['A', 'B', 'C']);
    const depart = ordre === 'devant'
        ? `${k}(${L} ${op} ${m}) + ${p}${L}`
        : `${p}${L} + ${k}(${L} ${op} ${m})`;
    const developpee = ordre === 'devant'
        ? `${k}${L} ${op} ${k * m} + ${p}${L}`
        : `${p}${L} + ${k}${L} ${op} ${k * m}`;
    const reduite = `${k + p}${L} ${op} ${k * m}`;
    return { L, k, m, p, op, nom, depart, developpee, reduite };
};

const genJustifierDrag = () => {
    const t = tirerChaine();
    const texte = `<p><b>${t.nom} = ${t.depart}. Justifie chaque étape du calcul</b> `
        + 'en glissant le bon verbe.</p>';
    const phrase = `${t.nom} = ${t.developpee} : j'ai {{développé}}. `
        + `${t.nom} = ${t.reduite} : j'ai {{réduit}}.`;
    const distracteurs = filtrerDistracteurs(phrase,
        ['factorisé', 'substitué', 'testé', 'additionné']).slice(0, 3);
    const payload = makeTexteTrou(phrase, texte,
        { ...opts(L_JUSTIFIER), mode: 'drag', distracteurs });
    payload._meta.params = { ...t };
    return payload;
};

const genJustifierQcm = () => {
    const t = tirerChaine();
    const v = randInt(1, 5);
    const texte = `<p><b>Pour démontrer que ${t.depart} = ${t.reduite}, `
        + 'comment procède-t-on ?</b></p>';
    const correct = `On développe ${t.k}(${t.L} ${t.op} ${t.m}), puis on réduit les termes en ${t.L}.`;
    const candidats = [
        `On réduit d'abord les termes en ${t.L}, puis on développe.`,
        `On remplace ${t.L} par ${v} : si l'égalité marche, c'est démontré.`,
        `On calcule les deux membres pour ${t.L} = 0 et c'est suffisant.`,
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte, opts(L_JUSTIFIER));
    payload._meta.params = { ...t, v };
    return payload;
};

// ═════════ Carte 2 — Vraie pour toute valeur de x ? (ex.5) ═════════

/** Une ligne « égalité universelle V/F » avec sa vérité PAR CONSTRUCTION. */
const tirerLigneCorrige = (vraieVoulue) => {
    const L = randomChoice(['x', 'y']);
    const k = randInt(2, 5);
    const m = randInt(2, 9);
    const p = randInt(1, 9);
    if (vraieVoulue) {
        const forme = randomChoice(['dev', 'red']);
        if (forme === 'dev') {
            // k(L + m) + p = kL + (km + p) : développement correct
            return { ligne: `${k}(${L} + ${m}) + ${p} = ${k}${L} + ${k * m + p}`, vraie: true };
        }
        // aL − bL + c = (a−b)L + c : réduction correcte (a − b ≥ 2)
        const b = randInt(2, 5);
        const a = b + randInt(2, 6);
        return { ligne: `${a}${L} ${MOINS} ${b}${L} + ${p} = ${a - b}${L} + ${p}`, vraie: true };
    }
    const forme = randomChoice(['parentheses', 'fusion']);
    if (forme === 'parentheses') {
        // parenthèses supprimées : k(L + m) + p = kL + (m + p) — fausse (k ≥ 2)
        return { ligne: `${k}(${L} + ${m}) + ${p} = ${k}${L} + ${m + p}`, vraie: false };
    }
    // termes non semblables fusionnés : aL + c = (a+c)L — fausse (c ≥ 1)
    const a = randInt(2, 9);
    return { ligne: `${a}${L} + ${p} = ${a + p}${L}`, vraie: false };
};

const genCorrigeVf = () => {
    const lignes = [];
    const bonnes = [];
    const vues = new Set();
    const nbVrai = randInt(1, 3);
    const voulues = shuffle([
        ...Array(nbVrai).fill(true),
        ...Array(4 - nbVrai).fill(false),
    ]);
    voulues.forEach((vraieVoulue, iv) => {
        let ajoutee = false;
        for (let essai = 0; essai < 40; essai++) {
            const t = tirerLigneCorrige(vraieVoulue);
            if (vues.has(t.ligne)) continue;
            vues.add(t.ligne);
            lignes.push(t.ligne);
            bonnes.push(t.vraie ? 0 : 1);
            ajoutee = true;
            break;
        }
        if (!ajoutee) {
            // repli déterministe paramétré par l'index
            const k = 2 + iv;
            const ligne = vraieVoulue
                ? `${k}(x + 6) + 1 = ${k}x + ${6 * k + 1}`
                : `${k}(x + 6) + 1 = ${k}x + 7`;
            if (!vues.has(ligne)) {
                vues.add(ligne);
                lignes.push(ligne);
                bonnes.push(vraieVoulue ? 0 : 1);
            }
        }
    });
    const payload = makeVraiFaux(lignes, bonnes,
        '<p><b>Vrai ou faux ?</b> Pour chaque ligne, dis si l\'égalité est vraie '
        + 'pour TOUTE valeur de la lettre (développe ou réduis pour le savoir).</p>',
        opts(L_CORRIGE));
    payload._meta.params = { lignes, bonnes };
    return payload;
};

const genCorrigeQcm = () => {
    const L = randomChoice(['x', 'y']);
    const vraie = randomChoice([true, false]);
    let texte, correct, candidats, params;
    if (!vraie) {
        // l'erreur de l'ex.5a : parenthèses supprimées
        const k = randInt(2, 5);
        const m = randInt(2, 9);
        const p = randInt(1, 9);
        texte = `<p><b>L'égalité ${k}(${L} + ${m}) + ${p} = ${k}${L} + ${m + p} `
            + 'est-elle vraie pour toute valeur de la lettre ?</b></p>';
        correct = `Non : en développant, ${k}(${L} + ${m}) + ${p} = ${k}${L} + ${k * m + p}.`;
        candidats = [
            'Oui : on peut toujours supprimer les parenthèses.',
            `Oui : c'est vrai pour ${L} = 1, donc c'est vrai partout.`,
            'Non : une égalité avec des lettres n\'est jamais vraie.',
        ];
        params = { vraie, L, k, m, p };
    } else {
        // la réduction correcte de l'ex.5b : aL − bL + c = (a−b)L + c
        const b = randInt(2, 5);
        const a = b + randInt(2, 6);
        const c = randInt(1, 9);
        texte = `<p><b>L'égalité ${a}${L} ${MOINS} ${b}${L} + ${c} = ${a - b}${L} + ${c} `
            + 'est-elle vraie pour toute valeur de la lettre ?</b></p>';
        correct = `Oui : en réduisant, ${a}${L} ${MOINS} ${b}${L} = ${a - b}${L}.`;
        candidats = [
            `Non : ${a}${L} ${MOINS} ${b}${L} = ${a - b}, la lettre disparaît.`,
            `Oui, mais seulement si ${L} est un nombre pair.`,
            'Non : on ne peut pas soustraire des expressions avec des lettres.',
        ];
        params = { vraie, L, a, b, c };
    }
    const payload = makeQcm([correct, ...candidats], correct, texte, opts(L_CORRIGE));
    payload._meta.params = params;
    return payload;
};

// ═════════ Carte 3 — Démontrer : pairs et impairs (ex.7/8 + cadrage) ═════════

// chaque propriété fournit les 4 étapes de sa preuve (fonctions des lettres)
const PROPRIETES = [
    {
        id: 'somme2pairs',
        titre: 'la somme de deux nombres pairs est toujours paire',
        etapes: (u, v) => [
            `On écrit les deux nombres pairs : 2${u} et 2${v}.`,
            `On calcule leur somme : 2${u} + 2${v}.`,
            `On factorise : 2${u} + 2${v} = 2(${u} + ${v}).`,
            'C\'est un multiple de 2 : la somme est donc paire.',
        ],
        trous: (u, v) => ({
            phrase: `Deux nombres pairs s'écrivent 2${u} et {{2${v}}}. `
                + `Leur somme : 2${u} + 2${v} = {{2}}(${u} + ${v}). `
                + `C'est un multiple de {{2}} : la somme est {{paire}}.`,
            pieges: [`2${v} + 1`, 'impaire', '4', `${u} + ${v}`],
        }),
    },
    {
        id: 'somme2impairs',
        titre: 'la somme de deux nombres impairs est toujours paire',
        etapes: (u, v) => [
            `On écrit les deux nombres impairs : 2${u} + 1 et 2${v} + 1.`,
            `On calcule leur somme : 2${u} + 2${v} + 2.`,
            `On factorise : 2(${u} + ${v} + 1).`,
            'C\'est un multiple de 2 : la somme est donc paire.',
        ],
        trous: (u, v) => ({
            phrase: `Deux nombres impairs s'écrivent 2${u} + 1 et {{2${v} + 1}}. `
                + `Leur somme vaut 2${u} + 2${v} + {{2}} = 2(${u} + ${v} + {{1}}). `
                + `La somme est donc {{paire}}.`,
            pieges: [`2${v}`, 'impaire', '3', '0'],
        }),
    },
    {
        id: 'pairPlusImpair',
        titre: 'la somme d\'un nombre pair et d\'un nombre impair est toujours impaire',
        etapes: (u, v) => [
            `On écrit le nombre pair 2${u} et le nombre impair 2${v} + 1.`,
            `On calcule leur somme : 2${u} + 2${v} + 1.`,
            `On factorise les deux premiers termes : 2(${u} + ${v}) + 1.`,
            'C\'est un multiple de 2 augmenté de 1 : la somme est impaire.',
        ],
        trous: (u, v) => ({
            phrase: `On écrit le pair 2${u} et l'impair {{2${v} + 1}}. `
                + `Leur somme : 2${u} + 2${v} + 1 = 2(${u} + ${v}) + {{1}}. `
                + `La somme est donc {{impaire}}.`,
            pieges: [`2${v}`, 'paire', '2', `${u} + ${v}`],
        }),
    },
    {
        id: 'produitPairImpair',
        titre: 'le produit d\'un nombre pair par un nombre impair est toujours pair',
        etapes: (u, v) => [
            `On écrit le nombre pair 2${u} et le nombre impair 2${v} + 1.`,
            `On calcule leur produit : 2${u}(2${v} + 1).`,
            `C'est 2 × ${u}(2${v} + 1) : un multiple de 2.`,
            'Le produit est donc pair.',
        ],
        trous: (u, v) => ({
            phrase: `On écrit le pair 2${u} et l'impair {{2${v} + 1}}. `
                + `Leur produit 2${u}(2${v} + 1) est un multiple de {{2}}. `
                + `Le produit est donc {{pair}}.`,
            pieges: [`2${v}`, 'impair', '3', `${u}`],
        }),
    },
];

const LETTRES_PREUVE = [['n', 'p'], ['a', 'b'], ['m', 'k'], ['c', 'd'], ['u', 'v']];

const genPariteOrdonner = () => {
    const prop = randomChoice(PROPRIETES);
    const [u, v] = randomChoice(LETTRES_PREUVE);
    const prenom = randomChoice(PRENOMS);
    const payload = makeOrdonner(prop.etapes(u, v),
        `<p><b>Aide ${prenom} à remettre dans l'ordre la démonstration : `
        + `« ${prop.titre} »</b> (${u} et ${v} sont des nombres entiers).</p>`,
        opts(L_PARITE));
    payload._meta.params = { prop: prop.id, u, v, prenom };
    return payload;
};

const genPariteDrag = () => {
    const prop = randomChoice(PROPRIETES);
    const [u, v] = randomChoice(LETTRES_PREUVE);
    const t = prop.trous(u, v);
    const texte = `<p><b>Complète la démonstration : « ${prop.titre} »</b> `
        + `(${u} et ${v} sont des nombres entiers).</p>`;
    const distracteurs = filtrerDistracteurs(t.phrase, shuffle([...new Set(t.pieges)])).slice(0, 4);
    const payload = makeTexteTrou(t.phrase, texte,
        { ...opts(L_PARITE), mode: 'drag', distracteurs });
    payload._meta.params = { prop: prop.id, u, v };
    return payload;
};

// ═════════ Carte 4 — Exemple ou démonstration ? (ex.9) ═════════

// propriétés vérifiables sur des exemples calculés
const PROPRIETES_EXEMPLES = [
    {
        enonce: 'le produit de deux nombres entiers qui se suivent est toujours pair',
        exemple: () => { const n = randInt(3, 12); return `${n} × ${n + 1} = ${n * (n + 1)}`; },
    },
    {
        enonce: 'la somme de deux nombres pairs est toujours paire',
        exemple: () => { const a = randInt(2, 12) * 2; const b = randInt(2, 12) * 2; return `${a} + ${b} = ${a + b}`; },
    },
    {
        enonce: 'la somme de deux nombres impairs est toujours paire',
        exemple: () => { const a = randInt(1, 12) * 2 + 1; const b = randInt(1, 12) * 2 + 1; return `${a} + ${b} = ${a + b}`; },
    },
    {
        enonce: 'la somme de trois nombres entiers qui se suivent est toujours un multiple de 3',
        exemple: () => { const n = randInt(2, 12); return `${n} + ${n + 1} + ${n + 2} = ${3 * n + 3}`; },
    },
];

const CORRECT_PREUVE = 'Non : ces calculs vérifient la propriété sur des exemples ; '
    + 'pour la démontrer, il faut un calcul littéral valable pour tous les nombres.';

const genPreuveQcm = () => {
    const prenom = randomChoice(PRENOMS);
    const prop = randomChoice(PROPRIETES_EXEMPLES);
    // 3 exemples distincts par construction (re-tirage borné)
    const exemples = [];
    for (let essai = 0; essai < 30 && exemples.length < 3; essai++) {
        const e = prop.exemple();
        if (!exemples.includes(e)) exemples.push(e);
    }
    const texte = `<p><b>${prenom} a calculé : ${exemples.join(' ; ')}. `
        + `${prenom} en conclut que ${prop.enonce}. A-t-${PRONOM[prenom]} DÉMONTRÉ cette propriété ?</b></p>`;
    const candidats = [
        `Oui : ${exemples.length} exemples qui marchent suffisent à démontrer.`,
        'Oui : personne n\'a trouvé de contre-exemple.',
        'Non : il faudrait vérifier au moins dix exemples.',
    ];
    const payload = makeQcm([CORRECT_PREUVE, ...candidats], CORRECT_PREUVE, texte, opts(L_PREUVE));
    payload._meta.params = { prenom, prop: prop.enonce, exemples };
    return payload;
};

const genPreuveVf = () => {
    const [p1, p2, p3, p4] = shuffle(PRENOMS).slice(0, 4);
    const nb = randInt(3, 9);
    const lettre = randomChoice(['n', 'p', 'a']);
    const lignes = shuffle([
        { l: `Pour ${p1}, vérifier une propriété sur ${nb} exemples suffit à la démontrer`, v: false },
        { l: `Pour ${p2}, un calcul littéral avec une lettre ${lettre} démontre la propriété pour tous les nombres`, v: true },
        { l: `Pour ${p3}, un seul contre-exemple suffit à prouver qu'une affirmation est fausse`, v: true },
        { l: `Pour ${p4}, il faut tester tous les nombres un par un pour démontrer`, v: false },
    ]);
    const payload = makeVraiFaux(
        lignes.map(x => x.l),
        lignes.map(x => (x.v ? 0 : 1)),
        '<p><b>Vrai ou faux ?</b> Qui a raison sur ce qui démontre vraiment ?</p>',
        opts(L_PREUVE));
    payload._meta.params = { lignes };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    justifier_texte_trou_drag: genJustifierDrag,
    justifier_qcm_unique: genJustifierQcm,
    corrige_tableau_vf: genCorrigeVf,
    corrige_qcm_unique: genCorrigeQcm,
    parite_ordonner: genPariteOrdonner,
    parite_texte_trou_drag: genPariteDrag,
    preuve_qcm_unique: genPreuveQcm,
    preuve_tableau_vf: genPreuveVf,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux',
    texte_trou_drag: 'Drag', ordonner: 'Ordonner',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'justifier', label: L_JUSTIFIER, formatsList: buildFormats('justifier') },
    { id: 'corrige', label: L_CORRIGE, formatsList: buildFormats('corrige') },
    { id: 'parite', label: L_PARITE, formatsList: buildFormats('parite') },
    { id: 'preuve', label: L_PREUVE, formatsList: buildFormats('preuve') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
